// src/components/PostSelection.js
import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useIntl } from "react-intl";
import { Add, Visibility } from "@material-ui/icons";

import { useBmapi } from "../utils/bmapi-context";
import { getErrorMessageString } from "../utils/errors";
import {
  extractFirstImageUrl,
  extractFirstText,
  extractSecondText,
} from "../utils/postUtils";
import PostPreview from "./PostPreview";

const useStyles = makeStyles(() => ({
  primaryClamp2: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
}));

export default function PostSelection({
  onSelectPost,
  reloadKey,
  optionalBusiness,
}) {
  const classes = useStyles();
  const intl = useIntl();
  const { bmapi, notifyError } = useBmapi();

  const [postList, setPostList] = useState([]);
  const [search, setSearch] = useState("");

  // stato per preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);

  const userInfo = bmapi?.getUserInfo?.();
  const role = userInfo?.role;
  const currentBusinessName = userInfo?.business?.name || "";
  const currentBusinessId = userInfo?.business?.id || "";

  const [postsBusinessFilter, setPostsBusinessFilter] = useState(
    currentBusinessId
  );

  const loadPostsList = useCallback(() => {
    if (!bmapi) return;

    bmapi
      .getPosts({
        business: postsBusinessFilter,
        lang: bmapi.getUserInfo().business.default_language,
      })
      .then((resp) => {
        const list = resp || [];
        const sorted = [...list].sort((a, b) =>
          (extractFirstText(a) || "").localeCompare(
            extractFirstText(b) || "",
            intl.locale || undefined,
            { sensitivity: "base" }
          )
        );
        setPostList(sorted);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError, postsBusinessFilter]);

  useEffect(() => {
    loadPostsList();
  }, [loadPostsList, reloadKey]);

  const handlePostAdd = (post) => {
    if (!onSelectPost) return;

    const title = extractFirstText(post) || "";
    const description = extractSecondText(post) || "";
    const imageUrl = extractFirstImageUrl(post) || "";

    onSelectPost({
      post,
      refId: post.id,
      title,
      description,
      imageUrl,
    });
  };

  const handlePreviewClick = (post) => {
    setPreviewPost(post);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewPost(null);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const filteredPosts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return postList;

    return postList.filter((post) => {
      const title = (extractFirstText(post) || "").toLowerCase();
      return title.includes(term);
    });
  }, [postList, search]);

  const canChooseBusiness = !!optionalBusiness && role === "TM";

  return (
    <Box>
      {/* Titolo sezione + search */}
      <Box mb={2}>
        <Typography variant="h6">Articoli disponibili</Typography>
        <Typography variant="caption" color="textSecondary">
          Premi un articolo per aggiungerlo alla galleria
        </Typography>

        {/* Label / Dropdown "Articoli di" */}
        <Box mt={1}>
          <Box display="flex" alignItems="center" gridGap={12}>
            <TextField
              margin="dense"
              label="Articoli di"
              value={postsBusinessFilter}
              onChange={(e) => setPostsBusinessFilter(e.target.value)}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={!canChooseBusiness}
            >
              <MenuItem value={currentBusinessId}>
                {currentBusinessName}
              </MenuItem>
              {canChooseBusiness && (
                <MenuItem value={optionalBusiness.id}>
                  {optionalBusiness.name}
                </MenuItem>
              )}
            </TextField>
          </Box>
        </Box>

        <Box mt={1}>
          <TextField
            label="Cerca"
            variant="standard"
            fullWidth
            value={search}
            onChange={handleSearchChange}
          />
        </Box>
      </Box>

      {/* Lista post */}
      <List>
        {filteredPosts.map((post, idx) => {
          const title = extractFirstText(post);
          return (
            <React.Fragment key={post.id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem>
                <ListItemText
                  primary={title}
                  primaryTypographyProps={{
                    className: classes.primaryClamp2,
                  }}
                />
                <IconButton
                  aria-label="preview"
                  onClick={() => handlePreviewClick(post)}
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  aria-label="aggiungi alla galleria"
                  onClick={() => handlePostAdd(post)}
                >
                  <Add />
                </IconButton>
              </ListItem>
            </React.Fragment>
          );
        })}

        {filteredPosts.length === 0 && (
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              Nessun articolo trovato.
            </Typography>
          </Box>
        )}
      </List>

      {/* Dialog di preview post */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Anteprima articolo</DialogTitle>
        <DialogContent dividers>
          {previewPost ? (
            <PostPreview post={previewPost} noFrame={true} />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Nessun contenuto da mostrare.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
